import { Link } from "react-router-dom";
import { Seo } from "../components/seo/Seo";
import { Breadcrumbs } from "../components/seo/Breadcrumbs";

type Workshop = {
  title: string;
  desc: string;
  price?: number;
  duration: string;
  level: "Beginner" | "Gevorderd" | "Expert";
  status: "Beschikbaar" | "Binnenkort";
  to?: string;
  img?: string;
};

const SINGLE_WORKSHOPS: Workshop[] = [
  {
    title: "Simon Says memory-game",
    desc: "Bouw je eigen elektronisch memory-spel met Arduino, 4 knoppen, 4 LEDs en een buzzer. Werkend apparaat mee naar huis.",
    price: 30,
    duration: "3 uur",
    level: "Beginner",
    status: "Beschikbaar",
    to: "/workshops/simon-says",
    img: "/assets/projects/project-simon-says.png",
  },
  {
    title: "Verkeerslicht-systeem",
    desc: "Programmeer een werkend verkeerslicht met 3 LEDs en timing-cyclus. Uitbreiding: kruispunt + voetganger-knop.",
    price: 30,
    duration: "3 uur",
    level: "Beginner",
    status: "Binnenkort",
    img: "/assets/projects/project-verkeerslicht.png",
  },
  {
    title: "Politiesirene + zwaailichten",
    desc: "Rood/blauw flashende LEDs + piezo-buzzer voor een echte politiesirene. Aan/uit-knop schakelt het systeem.",
    price: 30,
    duration: "3 uur",
    level: "Beginner",
    status: "Binnenkort",
    img: "/assets/projects/project-politiesirene.png",
  },
  {
    title: "Mini Piano",
    desc: "4 push buttons + buzzer wordt jouw eigen mini-piano. Speel tonen en kleine melodieën — programmeer je eigen liedjes.",
    price: 30,
    duration: "3 uur",
    level: "Beginner",
    status: "Binnenkort",
    img: "/assets/projects/project-mini-piano.png",
  },
  {
    title: "Reaction Game",
    desc: "LED gaat aan, druk zo snel mogelijk de knop. Met klastoernooi-mode — wie heeft de snelste reflexen?",
    price: 30,
    duration: "3 uur",
    level: "Beginner",
    status: "Binnenkort",
    img: "/assets/projects/project-reaction-game.png",
  },
  {
    title: "Whack-a-Mole",
    desc: "Random LED licht op, sla erop met de knop voor je tijd op is. Klassiek arcade-spel — zelf gebouwd.",
    price: 30,
    duration: "3 uur",
    level: "Beginner",
    status: "Binnenkort",
    img: "/assets/projects/project-whack-a-mole.png",
  },
  {
    title: "Quiz Buzzer",
    desc: "Ieder kind een knop. Eerste die drukt activeert LED + geluid. Perfect voor klasquizzen of verjaardagsfeestjes.",
    price: 30,
    duration: "3 uur",
    level: "Beginner",
    status: "Binnenkort",
    img: "/assets/projects/project-quiz-buzzer.png",
  },
  {
    title: "LED Dice",
    desc: "Druk op de knop en je elektronische dobbelsteen rolt. LED-patroon toont 1 tot 6 — geen plastic dobbelsteen meer nodig.",
    price: 30,
    duration: "3 uur",
    level: "Beginner",
    status: "Binnenkort",
    img: "/assets/projects/project-led-dice.png",
  },
  {
    title: "Morse Code Trainer",
    desc: "Type letters via een knop, LED + buzzer geven Morse uit. Leer de geheime taal van vroeger — en stuur boodschappen aan je vrienden.",
    price: 30,
    duration: "3 uur",
    level: "Beginner",
    status: "Binnenkort",
    img: "/assets/projects/project-morse-trainer.png",
  },
];

const SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Makers Hub workshops",
  description: "Hands-on tech-workshops voor kinderen 10-12 in Vlaanderen.",
  itemListElement: SINGLE_WORKSHOPS.map((w, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: w.to ? `https://makershub.be${w.to}` : undefined,
    name: w.title,
  })),
};

function statusTagClass(status: Workshop["status"]) {
  return status === "Beschikbaar" ? "" : "tag-orange";
}

function WorkshopCard({ w }: { w: Workshop }) {
  return (
    <article className={`workshop-card ${w.img ? "workshop-card-with-img" : ""}`}>
      {w.img && (
        <div className="workshop-card-img">
          <img src={w.img} alt={w.title} loading="lazy" />
        </div>
      )}
      <div className={`workshop-card-tag ${statusTagClass(w.status)}`}>{w.status}</div>
      <h3>{w.title}</h3>
      <p className="workshop-card-meta">
        {w.price ? `€${w.price} · ` : ""}{w.duration} · max 8 kinderen · {w.level}
      </p>
      <p>{w.desc}</p>
      {w.to ? (
        <Link to={w.to} className="btn btn-primary">
          Bekijk →
        </Link>
      ) : (
        <a href="/#inschrijven" className="btn btn-ghost">
          Schrijf in voor info →
        </a>
      )}
    </article>
  );
}

export function WorkshopsPage() {
  return (
    <>
      <Seo
        title="Workshops — Arduino & robotica voor kinderen 10-12 | Makers Hub"
        description="9 Arduino-workshops voor kinderen 10-12: Simon Says, verkeerslicht, politiesirene, mini-piano, reaction game, whack-a-mole, quiz buzzer, LED dice, morse trainer. Vanaf €30 pilot-prijs."
        path="/workshops"
        schema={SCHEMA}
      />
      <article className="page page-listing">
        <div className="container">
          <Breadcrumbs
            items={[
              { label: "Home", to: "/" },
              { label: "Workshops" },
            ]}
          />

          <header className="page-hero">
            <span className="section-tag">Workshops</span>
            <h1>Alle workshops voor kinderen 10-12</h1>
            <p className="lead">
              Hands-on. Echte hardware. Werkend project mee naar huis. Elke workshop is een
              eenmalige kennismaking van één zaterdag.
            </p>
          </header>

          <div className="workshop-section">
            <header className="workshop-section-head">
              <h2>Eenmalige workshops</h2>
              <p>
                Eén zaterdag · 3 uur · <strong>€30</strong> pilot-prijs (normaal €50) incl. Elegoo Basic Starter Kit (t.w.v. €20).
                <br />
                <span className="reuse-hint">
                  Heb je al een kit van een vorige workshop? Dan kost deze workshop slechts <strong>€10</strong> (kit hergebruiken).
                </span>
              </p>
            </header>
            <section className="workshop-grid workshop-grid-3">
              {SINGLE_WORKSHOPS.map((w) => (
                <WorkshopCard key={w.title} w={w} />
              ))}
            </section>
          </div>

          <section className="kit-reuse-callout">
            <div className="kit-reuse-badge">€20</div>
            <div>
              <h3>Bouwkit hergebruiken bespaart €20 per workshop</h3>
              <p>
                Bij je eerste workshop krijg je gratis de Elegoo Basic Starter Kit (t.w.v. €20).
                Die blijft van je kind en is herbruikbaar in elke volgende workshop — dan betaal
                je nog maar <strong>€10</strong> in plaats van €30.
              </p>
            </div>
          </section>

          <section className="page-section page-section-cta">
            <h2>Niet zeker welke?</h2>
            <p>
              Begin met Simon Says — eenmalig, betaalbaar, snel werkend resultaat. Vond je kind
              het tof? Dan kan je kiezen uit 8 andere eenmalige projecten.
            </p>
            <div className="page-ctas">
              <a href="/#inschrijven" className="btn btn-primary btn-lg">
                Inschrijven Simon Says →
              </a>
              <Link to="/faq" className="btn btn-ghost btn-lg">
                Veelgestelde vragen
              </Link>
            </div>
          </section>
        </div>
      </article>
    </>
  );
}
